import React, { useState, useEffect } from 'react';
import { useCheckout } from '../../services/checkout_api';
import './MyOrders.css';

const MyOrders = ({ setCurrentPage, user }) => {
  const { loading, error, getMyOrders, cancelOrder: cancelOrderAPI } = useCheckout();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filter, setFilter] = useState('all');
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await getMyOrders();
      console.log('Full API Response:', response);

      // API returns orders array directly
      if (Array.isArray(response)) {
        setOrders(response);
      } else if (response && response.orders) {
        // Handle case where orders are nested
        setOrders(response.orders);
      } else {
        setOrders([]);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setOrders([]);
    }
  };

  const handleCancelOrder = async (orderId, reason = 'Cancelled by customer') => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    setCancellingOrderId(orderId);
    try {
      await cancelOrderAPI(orderId, reason);
      // Refresh orders after cancellation
      await fetchOrders();
      alert('Order cancelled successfully');
    } catch (err) {
      console.error('Error cancelling order:', err);
      alert(err.message || 'Failed to cancel order');
    } finally {
      setCancellingOrderId(null);
      setShowCancelConfirm(null);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'status-badge status-pending';
      case 'confirmed':
        return 'status-badge status-confirmed';
      case 'processing':
        return 'status-badge status-processing';
      case 'shipped':
        return 'status-badge status-shipped';
      case 'delivered':
        return 'status-badge status-delivered';
      case 'cancelled':
        return 'status-badge status-cancelled';
      default:
        return 'status-badge';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const formatPrice = (price) => {
    const numPrice = parseFloat(price) || 0;
    return `$${numPrice.toFixed(2)}`;
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return order.status?.toLowerCase() === filter.toLowerCase();
  });

  const getOrderStats = () => {
    return {
      total: orders.length,
      pending: orders.filter(o => o.status?.toLowerCase() === 'pending').length,
      confirmed: orders.filter(o => o.status?.toLowerCase() === 'confirmed').length,
      delivered: orders.filter(o => o.status?.toLowerCase() === 'delivered').length,
      cancelled: orders.filter(o => o.status?.toLowerCase() === 'cancelled').length
    };
  };

  const stats = getOrderStats();

  const canCancelOrder = (order) => {
    const status = order.status?.toLowerCase();
    return status === 'pending' || status === 'confirmed';
  };

  if (loading && orders.length === 0) {
    return (
      <div className="my-orders-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-orders-container">
      <div className="my-orders-header">
        <h1>My Orders</h1>
        <p>Track and manage your orders</p>
      </div>

      {/* Order Statistics */}
      <div className="order-stats">
        <div className={`stat-card ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          <div className="stat-icon">📦</div>
          <div className="stat-details">
            <h3>{stats.total}</h3>
            <p>Total Orders</p>
          </div>
        </div>
        <div className={`stat-card ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>
          <div className="stat-icon">⏳</div>
          <div className="stat-details">
            <h3>{stats.pending}</h3>
            <p>Pending</p>
          </div>
        </div>
        <div className={`stat-card ${filter === 'confirmed' ? 'active' : ''}`} onClick={() => setFilter('confirmed')}>
          <div className="stat-icon">✅</div>
          <div className="stat-details">
            <h3>{stats.confirmed}</h3>
            <p>Confirmed</p>
          </div>
        </div>
        <div className={`stat-card ${filter === 'delivered' ? 'active' : ''}`} onClick={() => setFilter('delivered')}>
          <div className="stat-icon">🎉</div>
          <div className="stat-details">
            <h3>{stats.delivered}</h3>
            <p>Delivered</p>
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="order-filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Orders
        </button>
        <button
          className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pending
        </button>
        <button
          className={`filter-btn ${filter === 'confirmed' ? 'active' : ''}`}
          onClick={() => setFilter('confirmed')}
        >
          Confirmed
        </button>
        <button
          className={`filter-btn ${filter === 'delivered' ? 'active' : ''}`}
          onClick={() => setFilter('delivered')}
        >
          Delivered
        </button>
        <button
          className={`filter-btn ${filter === 'cancelled' ? 'active' : ''}`}
          onClick={() => setFilter('cancelled')}
        >
          Cancelled
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span>⚠️</span>
          <p>{error}</p>
          <button
            className="btn btn-link"
            onClick={fetchOrders}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="no-orders">
          <div className="no-orders-icon">📭</div>
          <h3>No orders found</h3>
          <p>
            {filter === 'all'
              ? "You haven't placed any orders yet."
              : `You don't have any ${filter} orders.`}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => setCurrentPage('products')}
          >
            Start Shopping
          </button>
        </div>
      ) : (
        <div className="orders-list">
          {filteredOrders.map((order) => (
            <div key={order.id} className="order-card">
              <div className="order-card-header">
                <div className="order-info">
                  <h3>Order #{order.order_number}</h3>
                  <p className="order-date">
                    {formatDate(order.timestamps?.created_at || order.created_at)}
                  </p>
                </div>
                <span className={getStatusBadgeClass(order.status)}>
                  {order.status}
                </span>
              </div>

              <div className="order-card-body">
                {/* Order Items */}
                <div className="order-items">
                  <h4>Items ({order.item_count || order.items?.length || 0})</h4>
                  <div className="items-list">
                    {order.items && Array.isArray(order.items) && order.items.length > 0 ? (
                      order.items.slice(0, 3).map((item, index) => (
                        <div key={index} className="order-item">
                          {(item.item_details?.image || item.image) && (
                            <img
                              src={item.item_details?.image || item.image}
                              alt={item.item_name || item.title || item.name || 'Product'}
                              className="item-image"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <div className="item-details">
                            <p className="item-title">{item.item_name || item.title || item.name || 'Product'}</p>
                            <p className="item-quantity">Qty: {item.quantity || 1}</p>
                          </div>
                          <p className="item-price">{formatPrice(item.unit_price || item.price || 0)}</p>
                        </div>
                      ))
                    ) : (
                      <p className="no-items">No items to display</p>
                    )}
                    {order.items && order.items.length > 3 && (
                      <p className="more-items">
                        +{order.items.length - 3} more item(s)
                      </p>
                    )}
                  </div>
                </div>

                {/* Order Summary */}
                <div className="order-summary">
                  <div className="summary-row">
                    <span>Subtotal:</span>
                    <span>{formatPrice(order.subtotal || order.pricing?.subtotal || order.totals?.subtotal || 0)}</span>
                  </div>
                  {(order.shipping_cost || order.pricing?.shipping_amount || order.totals?.shipping || 0) > 0 && (
                    <div className="summary-row">
                      <span>Shipping:</span>
                      <span>{formatPrice(order.shipping_cost || order.pricing?.shipping_amount || order.totals?.shipping || 0)}</span>
                    </div>
                  )}
                  {(order.tax_amount || order.pricing?.tax_amount || order.totals?.tax || 0) > 0 && (
                    <div className="summary-row">
                      <span>Tax:</span>
                      <span>{formatPrice(order.tax_amount || order.pricing?.tax_amount || order.totals?.tax || 0)}</span>
                    </div>
                  )}
                  <div className="summary-row total">
                    <span>Total:</span>
                    <span>{formatPrice(order.total_amount || order.pricing?.total_amount || order.totals?.total || order.total || 0)}</span>
                  </div>
                </div>

                {/* Shipping Address */}
                {order.shipping_address && (
                  <div className="shipping-address">
                    <h4>Shipping Address</h4>
                    {(() => {
                      // Handle JSON string
                      if (typeof order.shipping_address === 'string') {
                        try {
                          const addr = JSON.parse(order.shipping_address);
                          return (
                            <>
                              <p>{addr.address || ''}</p>
                              <p>
                                {[addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ')}
                              </p>
                              {addr.country && <p>{addr.country}</p>}
                            </>
                          );
                        } catch (e) {
                          return <p>{order.shipping_address}</p>;
                        }
                      }
                      // Handle object
                      return (
                        <>
                          <p>{order.shipping_address.address || 'N/A'}</p>
                          <p>
                            {[order.shipping_address.city, order.shipping_address.state, order.shipping_address.zipCode].filter(Boolean).join(', ')}
                          </p>
                          {order.shipping_address.country && (
                            <p>{order.shipping_address.country}</p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div className="order-card-footer">
                <button
                  className="btn btn-outline"
                  onClick={() => setSelectedOrder(order)}
                >
                  View Details
                </button>
                {canCancelOrder(order) && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleCancelOrder(order.id)}
                    disabled={cancellingOrderId === order.id}
                  >
                    {cancellingOrderId === order.id ? 'Cancelling...' : 'Cancel Order'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content order-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details</h2>
              <button
                className="modal-close"
                onClick={() => setSelectedOrder(null)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h3>Order Information</h3>
                <div className="detail-row">
                  <span>Order Number:</span>
                  <strong>{selectedOrder.order_number}</strong>
                </div>
                <div className="detail-row">
                  <span>Status:</span>
                  <span className={getStatusBadgeClass(selectedOrder.status)}>
                    {selectedOrder.status}
                  </span>
                </div>
                <div className="detail-row">
                  <span>Order Date:</span>
                  <span>{formatDate(selectedOrder.timestamps?.created_at || selectedOrder.created_at)}</span>
                </div>
                <div className="detail-row">
                  <span>Payment Method:</span>
                  <span>{(selectedOrder.payment_method || 'COD').toUpperCase()}</span>
                </div>
                <div className="detail-row">
                  <span>Payment Status:</span>
                  <span>{selectedOrder.payment_status || 'Pending'}</span>
                </div>
              </div>

              <div className="detail-section">
                <h3>Customer Information</h3>
                <div className="detail-row">
                  <span>Name:</span>
                  <span>{selectedOrder.customer_name || selectedOrder.customer_info?.name || selectedOrder.customer_info?.fullName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span>Email:</span>
                  <span>{selectedOrder.customer_email || selectedOrder.customer_info?.email || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span>Phone:</span>
                  <span>{selectedOrder.customer_phone || selectedOrder.customer_info?.phone || 'N/A'}</span>
                </div>
                {(selectedOrder.customer_company || selectedOrder.customer_info?.company) && (
                  <div className="detail-row">
                    <span>Company:</span>
                    <span>{selectedOrder.customer_company || selectedOrder.customer_info?.company}</span>
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h3>Items Ordered</h3>
                <div className="items-detail-list">
                  {selectedOrder.items && selectedOrder.items.length > 0 ? (
                    selectedOrder.items.map((item, index) => (
                      <div key={index} className="detail-item">
                        {(item.item_details?.image || item.image) && (
                          <img
                            src={item.item_details?.image || item.image}
                            alt={item.item_name || item.title || item.name || 'Product'}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="detail-item-info">
                          <p className="detail-item-title">{item.item_name || item.title || item.name || 'Product'}</p>
                          <p className="detail-item-meta">
                            SKU: {item.item_sku || item.sku || 'N/A'}
                          </p>
                          <p className="detail-item-meta">
                            {formatPrice(item.unit_price || item.price || 0)} × {item.quantity || 1}
                          </p>
                        </div>
                        <p className="detail-item-total">
                          {formatPrice(item.total_price || (item.unit_price || item.price || 0) * (item.quantity || 1))}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p>No items found</p>
                  )}
                </div>
              </div>

              {/* Installation Information */}
              {(selectedOrder.installation?.requires_installation ||
                selectedOrder.installation?.installation_status ||
                selectedOrder.installation?.installation_date ||
                selectedOrder.installation?.technician?.name) && (
                  <div className="detail-section installation-section">
                    <h3>🔧 Installation Service</h3>

                    <div className="detail-row">
                      <span>Status:</span>
                      <span className={`installation-status-badge status-${selectedOrder.installation?.installation_status || 'pending'}`}>
                        {selectedOrder.installation?.installation_status === 'not_required' && '❌ Not Required'}
                        {selectedOrder.installation?.installation_status === 'pending' && '⏳ Pending'}
                        {selectedOrder.installation?.installation_status === 'scheduled' && '📅 Scheduled'}
                        {selectedOrder.installation?.installation_status === 'in_progress' && '🔨 In Progress'}
                        {selectedOrder.installation?.installation_status === 'completed' && '✅ Completed'}
                        {selectedOrder.installation?.installation_status === 'cancelled' && '❌ Cancelled'}
                        {!selectedOrder.installation?.installation_status && '⏳ Pending'}
                      </span>
                    </div>

                    {selectedOrder.installation?.installation_date && (
                      <div className="detail-row">
                        <span>Scheduled Date:</span>
                        <span>{formatDate(selectedOrder.installation.installation_date)}</span>
                      </div>
                    )}

                    {selectedOrder.installation?.installation_time_slot && (
                      <div className="detail-row">
                        <span>Time Slot:</span>
                        <span>{selectedOrder.installation.installation_time_slot_label || selectedOrder.installation.installation_time_slot}</span>
                      </div>
                    )}

                    {selectedOrder.installation?.technician?.name && (
                      <div className="detail-row">
                        <span>Technician:</span>
                        <span>{selectedOrder.installation.technician.name}</span>
                      </div>
                    )}

                    {selectedOrder.installation?.technician?.phone && (
                      <div className="detail-row">
                        <span>Technician Phone:</span>
                        <span>{selectedOrder.installation.technician.phone}</span>
                      </div>
                    )}

                    {selectedOrder.installation?.installation_address && (
                      <div className="detail-row full-width">
                        <span>Installation Address:</span>
                        <span>{selectedOrder.installation.installation_address}</span>
                      </div>
                    )}

                    {selectedOrder.installation?.installation_notes && (
                      <div className="detail-row full-width">
                        <span>Installation Notes:</span>
                        <span>{selectedOrder.installation.installation_notes}</span>
                      </div>
                    )}

                    {selectedOrder.installation?.installation_completed_at && (
                      <div className="detail-row">
                        <span>Completed At:</span>
                        <span>{formatDate(selectedOrder.installation.installation_completed_at)}</span>
                      </div>
                    )}

                    {selectedOrder.installation?.installation_cancelled_at && (
                      <div className="detail-row">
                        <span>Cancelled At:</span>
                        <span>{formatDate(selectedOrder.installation.installation_cancelled_at)}</span>
                      </div>
                    )}

                    {selectedOrder.installation?.installation_status === 'pending' && (
                      <div className="installation-note">
                        <p>📞 Our installation team will contact you soon to schedule the installation.</p>
                      </div>
                    )}

                    {selectedOrder.installation?.installation_status === 'scheduled' && (
                      <div className="installation-note">
                        <p>✅ Your installation has been scheduled. Our technician will arrive at the scheduled time.</p>
                      </div>
                    )}
                  </div>
                )}

              {selectedOrder.delivery_info && (
                <div className="detail-section">
                  <h3>Delivery Information</h3>
                  {selectedOrder.delivery_info.estimated_delivery_date && (
                    <div className="detail-row">
                      <span>Estimated Delivery:</span>
                      <span>{formatDate(selectedOrder.delivery_info.estimated_delivery_date)}</span>
                    </div>
                  )}
                  {selectedOrder.delivery_info.preference && (
                    <div className="detail-row">
                      <span>Delivery Preference:</span>
                      <span>{selectedOrder.delivery_info.preference}</span>
                    </div>
                  )}
                  {selectedOrder.delivery_info.special_instructions && (
                    <div className="detail-row full-width">
                      <span>Special Instructions:</span>
                      <span>{selectedOrder.delivery_info.special_instructions}</span>
                    </div>
                  )}
                </div>
              )}

              {selectedOrder.shipping_address && (
                <div className="detail-section">
                  <h3>Shipping Address</h3>
                  <div className="address-block">
                    {(() => {
                      // Handle JSON string
                      if (typeof selectedOrder.shipping_address === 'string') {
                        try {
                          const addr = JSON.parse(selectedOrder.shipping_address);
                          return (
                            <>
                              <p>{addr.address || 'N/A'}</p>
                              <p>
                                {[addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ')}
                              </p>
                              {addr.country && <p>{addr.country}</p>}
                            </>
                          );
                        } catch (e) {
                          return <p>{selectedOrder.shipping_address}</p>;
                        }
                      }
                      // Handle object
                      return (
                        <>
                          <p>{selectedOrder.shipping_address.address || 'N/A'}</p>
                          <p>
                            {[selectedOrder.shipping_address.city, selectedOrder.shipping_address.state, selectedOrder.shipping_address.zipCode].filter(Boolean).join(', ')}
                          </p>
                          {selectedOrder.shipping_address.country && (
                            <p>{selectedOrder.shipping_address.country}</p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {selectedOrder.notes && (
                <div className="detail-section">
                  <h3>Order Notes</h3>
                  <p>{selectedOrder.notes}</p>
                </div>
              )}

              <div className="detail-section">
                <h3>Order Summary</h3>
                <div className="detail-row">
                  <span>Subtotal:</span>
                  <span>{formatPrice(selectedOrder.subtotal || selectedOrder.pricing?.subtotal || selectedOrder.totals?.subtotal || 0)}</span>
                </div>
                {(selectedOrder.shipping_cost || selectedOrder.pricing?.shipping_amount || selectedOrder.totals?.shipping || 0) > 0 && (
                  <div className="detail-row">
                    <span>Shipping:</span>
                    <span>{formatPrice(selectedOrder.shipping_cost || selectedOrder.pricing?.shipping_amount || selectedOrder.totals?.shipping || 0)}</span>
                  </div>
                )}
                {(selectedOrder.tax_amount || selectedOrder.pricing?.tax_amount || selectedOrder.totals?.tax || 0) > 0 && (
                  <div className="detail-row">
                    <span>Tax:</span>
                    <span>{formatPrice(selectedOrder.tax_amount || selectedOrder.pricing?.tax_amount || selectedOrder.totals?.tax || 0)}</span>
                  </div>
                )}
                {(selectedOrder.discount_amount || selectedOrder.pricing?.discount_amount || selectedOrder.totals?.discount || 0) > 0 && (
                  <div className="detail-row">
                    <span>Discount:</span>
                    <span>-{formatPrice(selectedOrder.discount_amount || selectedOrder.pricing?.discount_amount || selectedOrder.totals?.discount || 0)}</span>
                  </div>
                )}
                <div className="detail-row total">
                  <span>Total:</span>
                  <strong>{formatPrice(selectedOrder.total_amount || selectedOrder.pricing?.total_amount || selectedOrder.totals?.total || selectedOrder.total || 0)}</strong>
                </div>
              </div>

              {canCancelOrder(selectedOrder) && (
                <div className="modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setSelectedOrder(null);
                      handleCancelOrder(selectedOrder.id);
                    }}
                  >
                    Cancel This Order
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;